import {html, LitElement} from 'lit';
import {localized, msg} from '@lit/localize';
import {property, customElement, state} from 'lit/decorators.js';
import {notificationSvg} from '../../helpers/elements.ts';
import {classMap} from 'lit/directives/class-map.js';
import {isEmailValid} from '../../helpers/string.ts';
import {ifDefined} from 'lit/directives/if-defined.js';

@localized()
@customElement('element-input')
export class ElementInput extends LitElement {
  @property({type: String}) placeholder: string | undefined;
  @property({type: String}) label: string | undefined;
  @property({type: String}) errorMessage: string | undefined;
  @property({type: String}) type: 'text' | 'date' | 'email' | 'tel' = 'text';
  @property({type: Boolean}) disabled = false;
  @property({type: Boolean}) textArea = false;
  @property({type: Boolean}) bigTextArea = true;
  // If true the text area takes the available height instead of a fixed one
  @property({type: Boolean}) availableHeight = false;
  @property({type: Boolean}) required = false;
  @property({type: String}) value = '';
  @property({type: Boolean}) useCloseIcon = false;
  @property({type: Number}) maxLength: number | undefined;
  @state() error = false;

  validate() {
    this.error = this.required && !this.value?.length;
    if (!this.error && this.type === 'email') {
      this.error = !isEmailValid(this.value);
    }
  }

  renderInputElement() {
    return html` <input
      .disabled=${this.disabled}
      type="${this.type}"
      .value="${this.value}"
      maxlength="${ifDefined(this.maxLength)}"
      class="${classMap({
        error: this.error,
        'date-input': this.type === 'date',
        'pr-[20px]': !this.useCloseIcon,
        'pr-[40px] md:pr-[50px]': this.useCloseIcon,
      })} h-[40px] md:h-[56px] w-full flex-auto pl-[20px] py-[17px] outline-none"
      placeholder="${ifDefined(this.placeholder)}"
      @focusin="${() => (this.error = false)}"
      @focusout="${this.validate}"
    />`;
  }

  get textAreaElement() {
    return html` <textarea
      .disabled=${this.disabled}
      .value="${this.value}"
      class="${classMap({
        error: this.error,
        'h-[120px]': !this.availableHeight && this.bigTextArea,
        'sm:h-[160px]': !this.availableHeight && this.bigTextArea,
        'h-[62px]': !this.availableHeight && !this.bigTextArea,
        'md:h-[86px]': !this.availableHeight && !this.bigTextArea,
      })} w-full flex-auto px-[20px] py-[17px] outline-none"
      placeholder="${ifDefined(this.placeholder)}"
      @focusin="${() => (this.error = false)}"
      @focusout="${this.validate}"
    >
${this.value}</textarea
    >`;
  }

  override render() {
    return html` <div
      class="body w-full ${classMap({
        'h-full': this.availableHeight,
      })}"
    >
      <div
        .hidden="${!this.label?.length}"
        class="${classMap({
          'text-disabled': this.disabled,
        })} select-none text-ellipsis whitespace-nowrap font-bold"
      >
        <span>${this.label}</span>
        <span .hidden="${!this.required}" class="font-normal"
          >${msg('(Mandatory field)')}</span
        >
      </div>
      <div
        class="flex flex-row-reverse ${classMap({
          'h-full': this.availableHeight,
        })}"
      >
        ${this.textArea ? this.textAreaElement : this.renderInputElement()}
        ${this.error
          ? notificationSvg(
              'h-[24px] w-[24px] fill-danger rotate-180 self-center absolute mr-[12px] md:mr-[20px]',
            )
          : ''}
      </div>
      <div class="pl-[16px] text-danger" .hidden="${!this.error}">
        ${this.errorMessage}
      </div>
    </div>`;
  }

  override createRenderRoot() {
    return this;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'element-input': ElementInput;
  }
}
